import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PriceIncrease } from './types';

interface PriceChangesTabProps {
  priceIncreases: { totalProducts: number; increases: PriceIncrease[] };
  priceViewMode: 'increases' | 'decreases';
  onPriceViewChange: (mode: 'increases' | 'decreases') => void;
}

export default function PriceChangesTab({
  priceIncreases,
  priceViewMode,
  onPriceViewChange,
}: PriceChangesTabProps) {
  const navigate = useNavigate();

  if (!priceIncreases) {
    return <p className="text-gray-500">No price increase data available.</p>;
  }

  const allItems = priceIncreases.increases;
  if (allItems.length === 0) {
    return <p className="text-gray-500">No products with price changes found.</p>;
  }

  // Filter and sort based on selected view mode
  let displayItems: typeof allItems;
  if (priceViewMode === 'increases') {
    // Show only positive percentage changes, sorted by largest increase
    displayItems = allItems
      .filter(item => item.percentageIncrease > 0)
      .sort((a, b) => parseFloat(b.percentageIncrease.toString()) - parseFloat(a.percentageIncrease.toString()));
  } else {
    // Show only negative percentage changes, sorted by largest decrease (most negative first)
    displayItems = allItems
      .filter(item => item.percentageIncrease < 0)
      .sort((a, b) => parseFloat(a.percentageIncrease.toString()) - parseFloat(b.percentageIncrease.toString()));
  }

  if (displayItems.length === 0) {
    const message = priceViewMode === 'increases' 
      ? 'No products with price increases found.'
      : 'No products with price decreases found.';
    return <p className="text-gray-500">{message}</p>;
  }

  const isShowingIncreases = priceViewMode === 'increases';
  const topItem = displayItems[0];
  const topLabel = isShowingIncreases ? 'Largest Percentage Increase' : 'Largest Percentage Decrease';
  const topColorClass = isShowingIncreases ? 'text-red-600' : 'text-green-600';
  
  // Find the actual most tracked product (most purchases) - from all items
  const mostTrackedProduct = allItems.reduce((prev, current) => 
    (current.pricePoints > prev.pricePoints) ? current : prev
  );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Products tracked</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">{priceIncreases.totalProducts}</p>
        </div>
        {displayItems.length > 0 && (
          <>
            <div className="card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{topLabel}</p>
              <p className={`mt-3 text-2xl font-semibold ${topColorClass}`}>{topItem.percentageIncrease > 0 ? '+' : ''}{topItem.percentageIncrease}%</p>
              <p className="text-xs text-ink-400 mt-1">{topItem.increase}</p>
            </div>
            <div className="card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Most tracked product</p>
              <p className="mt-3 text-lg font-semibold text-ink-700">{mostTrackedProduct.productNameOriginal}</p>
              <p className="text-xs text-ink-400 mt-1">{mostTrackedProduct.pricePoints} price points</p>
            </div>
          </>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="space-y-5 border-b border-ink-100/70 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-ink-700">
                {isShowingIncreases ? 'Largest product price increases' : 'Largest product price decreases'}
              </h3>
              <p className="text-sm text-ink-500">
                Sorted by percentage {isShowingIncreases ? 'increase' : 'decrease'} (excluding free items).
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onPriceViewChange('increases')}
                className={`pill-tab ${
                  priceViewMode === 'increases' ? 'bg-red-100 text-red-600 ring-1 ring-red-200' : ''
                }`}
              >
                📈 Price increases
              </button>
              <button
                onClick={() => onPriceViewChange('decreases')}
                className={`pill-tab ${
                  priceViewMode === 'decreases' ? 'bg-green-100 text-green-600 ring-1 ring-green-200' : ''
                }`}
              >
                📉 Price decreases
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-100/80 bg-blue-50/70 p-4">
            <p className="text-sm font-semibold text-blue-900 mb-2">📊 How this works</p>
            <ul className="list-disc space-y-1 pl-5 text-xs text-blue-800">
              <li><strong>First vs. last price:</strong> 3-month averages at the start and end of your purchase history (smooths out promotions).</li>
              <li><strong>Max price:</strong> Highest recorded price for context.</li>
              <li><strong>Change &amp; %:</strong> Difference between the smoothed start and end prices.</li>
              <li><strong>Tracked:</strong> Number of purchases—more data equals more reliable trends.</li>
            </ul>
          </div>
        </div>

        <div className="table-card border-transparent">
          <table className="table-default">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-center group relative">
                  <span className="cursor-help">First price</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    3-month average at start
                  </div>
                </th>
                <th className="text-center group relative">
                  <span className="cursor-help">Last price</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    3-month average at end
                  </div>
                </th>
                <th className="text-center group relative">
                  <span className="cursor-help">Max price</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    Highest price recorded
                  </div>
                </th>
                <th className="text-center group relative">
                  <span className="cursor-help">Change</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    Smoothed £ difference
                  </div>
                </th>
                <th className="text-center group relative">
                  <span className="cursor-help">%</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    Percentage change
                  </div>
                </th>
                <th className="text-center group relative">
                  <span className="cursor-help">Tracked</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    Purchase count
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100/80">
              {displayItems.map((item) => (
                <tr key={item.productName}>
                  <td className="font-medium text-ink-700">
                    <button
                      onClick={() => navigate(`/price-history?product=${encodeURIComponent(item.productName)}`)}
                      className="text-left text-accent-600 underline decoration-dotted underline-offset-4 transition hover:text-accent-500"
                      title="Click to view full price history"
                    >
                      {item.productNameOriginal}
                    </button>
                  </td>
                  <td className="text-center text-ink-500">{item.firstPrice}</td>
                  <td className="text-center text-ink-500">{item.lastPrice}</td>
                  <td className="text-center font-medium text-ink-600">{item.maxPrice}</td>
                  <td className={`text-center text-sm font-semibold ${item.percentageIncrease >= 0 ? 'text-red-600' : 'text-green-600'}`}>{item.increase}</td>
                  <td className={`text-center text-sm font-semibold ${item.percentageIncrease >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {item.percentageIncrease > 0 ? '+' : ''}{item.percentageIncrease}%
                  </td>
                  <td className="text-center text-ink-500">{item.pricePoints}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

